import getFetcher from 'api/utils/getFetcher';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { LegacyEmbeddedReport } from 'types/legacyDashboard';

export interface EmbeddedReportResult {
  success: boolean;
  data: LegacyEmbeddedReport;
  error?: string;
}

export type EmbeddedReportError = string;

interface EmbeddedReportReturnVal {
  embeddedReport: Partial<LegacyEmbeddedReport>;
  isLoading: boolean;
  error?: EmbeddedReportError;
}

const useEmbeddedReport = (
  orgId?: OrgId,
  reportId?: number,
  simCardId?: SimCardId,
  hologramSimId?: HologramSimId,
  billingPeriodStart?: number,
  billingPeriodEnd?: number
): EmbeddedReportReturnVal => {
  const queryParamsObj: any = {};

  if (orgId) {
    queryParamsObj.orgid = orgId.toString();
  }

  if (simCardId) {
    queryParamsObj.simcardid = simCardId;
  }

  if (hologramSimId) {
    queryParamsObj.simid = hologramSimId;
  }

  if (billingPeriodStart && billingPeriodEnd) {
    queryParamsObj.billingPeriodStart = billingPeriodStart;
    queryParamsObj.billingPeriodEnd = billingPeriodEnd;
  }

  const queryParams = new URLSearchParams(queryParamsObj);

  const [data, setData] = useState<EmbeddedReportResult>();
  const [error, setError] = useState<EmbeddedReportError>();
  const [isLoading, setIsLoading] = useState(false);

  const url =
    reportId && orgId
      ? `${process.env.VITE_API_ROOT}/reports/embedded/${reportId}?${queryParams}`
      : null;

  const lastUrl = useRef<string | null>(null);

  useEffect(() => {
    const getEmbeddedReportData = async (getReportUrl: string) => {
      let result: EmbeddedReportResult | undefined;
      try {
        result = await getFetcher(getReportUrl);

        setData(result);
      } catch (ex) {
        if (typeof ex === 'string') {
          setError(ex);
        } else {
          setError('Unknown error occurred');
        }

        // eslint-disable-next-line no-console
        console.error(ex);
      }

      if (!result && error) {
        setData({} as any);
      }
      setIsLoading(false);
    };

    if (url && lastUrl.current !== url && !isLoading) {
      lastUrl.current = url;
      setIsLoading(true);
      setError(undefined);
      getEmbeddedReportData(url);
    }
  }, [data, error, isLoading, url]);

  const embeddedReport = useMemo(() => data?.data ?? {}, [data]);

  return {
    embeddedReport,
    error: data?.error ?? error,
    isLoading,
  };
};

export default useEmbeddedReport;
